import React, { useCallback, useRef, useState } from 'react';
import { ATMButton, ATMModal } from 'shared-it-appmod-ui';
import {
  ToastSuccess,
  ToastError,
} from 'src/components/atoms/toaster/toaster.component';
import { ISensitiveCities, ISensitiveCitiesForm } from 'src/models/sensitive-cities.model';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import Lang from 'src/libraries/language';
import { actionTypes } from 'src/ducks/sensitive-cities.duck';
import { getSensitiveCitiesStatus } from 'src/selectors/sensitive-cities.selector';
import SensitiveCitiesForm from '../sensitive-cities-form/sensitive-cities-form.component';
import style from '../sensitive-cities-list/sensitive-cities-list.module.scss';

type IProp = {
  data: ISensitiveCities;
};

const SensitiveCitiesEdit: React.FC<IProp> = ({ data }) => {
  const { state, actions } = useSensitiveCitiesContext();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  let defaultValues: Partial<ISensitiveCities> = {
    sensitiveCity: '',
    pamLead: '',
  };

  if (data) {
    defaultValues = data;
  }

  const editStatus = getSensitiveCitiesStatus(state, actionTypes.SENSITIVE_CITIES_UPDATE);

  const handleSave = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    async (value) => {
      const formData: ISensitiveCitiesForm = {
        sensitiveCity: value.sensitiveCity,
        pamLead: value.pamLead,
      };
      const response = await actions.updatePUT(value.sensitiveCityId, formData);
      if (!response.error) {
        ToastSuccess(
          Lang.formatString(
            Lang.MSG_SENSITIVE_CITY_EDIT_SUCCESS,
            formData.sensitiveCity ? formData.sensitiveCity : value.sensitiveCity
          )
        );
      } else {
        ToastError(response.error.message);
      }
      setIsEditOpen(false);
      actions.listGET();
    },
    [actions, setIsEditOpen]
  );

  return (
    <ATMModal
      open={isEditOpen}
      size="tiny"
      trigger={
        <ATMButton
          className={style.actionIcon}
          basic
          icon="edit outline"
          onClick={() => setIsEditOpen(true)}
        />
      }
      header={Lang.LBL_ADD_SENSITIVE_CITIES}
      content={
        <div className={style.content}>
          <SensitiveCitiesForm
            defaultValues={defaultValues}
            formRef={formRef}
            handleSubmit={handleSubmit}
            handleEnable={(value) => setIsDirty(value)}
          />
        </div>
      }
      actions={
        <div className={style.actionBtns}>
          <ATMButton
            basic
            color="blue"
            content={Lang.LBL_CANCEL}
            onClick={() => {
              setIsEditOpen(false);
            }}
          />
          <ATMButton
            primary
            content={Lang.LBL_SAVE}
            disabled={!isDirty || editStatus.fetching}
            onClick={handleSave}
            loading={editStatus.fetching}
          />
        </div>
      }
    />
  );
};

export default SensitiveCitiesEdit;
